<template>
  <div>
    <ts-page-title
      :title="$t('examRegister.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('examRegister.pageTitle'),
          active: true,
        },
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper class="tw-flex tw-items-center tw-justify-between">
        <a-button
          type="primary"
          @click="$router.push({ name: 'exam-register-create' })"
        >
          {{ $t("addNew") }}
        </a-button>
        <div class="tw-justify-end tw-space-x-2">
          <a-input
            v-model="search"
            v-tooltip="$t('examRegister.search')"
            :placeholder="$t('examRegister.search')"
            style="width: 250px"
            class="inline-block"
          />
          <a-button v-tooltip="$t('filter')" @click.prevent="visible = true">
            <i class="fa fa-filter"></i>
          </a-button>
        </div>
      </ts-panel-wrapper>
      <div class="tw-overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'exam_date',
          }"
          :expandable="true"
        >
          <template v-slot="{ record }">
            <td class="tw-text-center tw-whitespace-nowrap">
              {{ record.branch.prefix_code }}
            </td>
            <td class="tw-whitespace-nowrap">{{ record.examination_event }}</td>
            <td class="tw-whitespace-nowrap">
              {{ record.levelskill.level_skill_name }} | Skill:
              {{ record.levelskill.skill_name_en }}| LeveL:
              {{ record.levelskill.skill_level_order }}
              <span
                v-if="record.levelskill.is_accessor"
                class="badge bg-primary mr-2 tw-capitalize"
              >
                Accessor</span
              >
            </td>
            <td class="tw-whitespace-nowrap">
              {{ record.trainer.full_name_en }}
            </td>
            <td class="tw-whitespace-nowrap">
              {{ record.trainer2 ? record.trainer2.full_name_en : "" }}
            </td>
            <td class="tw-whitespace-nowrap">
              {{ record.trainer3 ? record.trainer3.full_name_en : "" }}
            </td>
            <td class="text-center tw-whitespace-nowrap">{{ record.room }}</td>
            <td class="text-center tw-whitespace-nowrap">
              {{ record.exam_date }}
            </td>
            <td class="text-center tw-whitespace-nowrap">
              {{ record.time_start }}
            </td>
            <td class="text-center tw-whitespace-nowrap">
              {{ record.time_stop }}
            </td>
          </template>

          <div slot="expand" slot-scope="prop">
            <table class="tw-w-11/12 tw-mx-auto detail">
              <thead>
                <tr class="tw-uppercase tw-bg-gray-200 tw-h-8 tw-text-white">
                  <th class="tw-pl-2" style="width: 5%">
                    {{ $t("examRegister.no") }}
                  </th>
                  <th class="tw-pl-2 tw-whitespace-nowrap">
                    {{ $t("examRegister.cardNumber") }}
                  </th>
                  <th class="tw-pl-2 tw-whitespace-nowrap">
                    {{ $t("examRegister.nameEn") }}
                  </th>
                  <th class="tw-pl-2 tw-whitespace-nowrap">
                    {{ $t("examRegister.sex") }}
                  </th>
                  <th class="tw-pl-2 tw-whitespace-nowrap">
                    {{ $t("examRegister.phone") }}
                  </th>
                  <th class="tw-pl-2 tw-text-center tw-whitespace-nowrap">
                    {{ $t("examRegister.isEvaluated") }}
                  </th>
                  <th
                    class="tw-pl-2 tw-text-center tw-whitespace-nowrap"
                    style="width: 10%"
                  >
                    {{ $t("actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  class="tw-border-b-2"
                  v-for="(detail, index) in prop.record.exam_registers"
                  :key="index"
                >
                  <td class="tw-pl-2">
                    {{ index + 1 }}
                  </td>
                  <td class="tw-p-2">
                    {{ detail.candidates.card_number }}
                  </td>
                  <td class="tw-p-2">
                    {{ detail.candidates.full_name_en }}
                  </td>
                  <td class="tw-p-2">
                    {{ detail.candidates.sex }}
                  </td>
                  <td class="tw-p-2">
                    {{ detail.candidates.phone }}
                  </td>
                  <td class="tw-p-2 tw-text-center">
                    <span
                      v-if="detail.is_evaluated"
                      class="badge bg-primary mr-2 tw-capitalize"
                    >
                      Yes</span
                    >
                    <span v-else class="badge bg-danger mr-2 tw-capitalize">
                      No</span
                    >
                  </td>
                  <td
                    class="text-center tw-space-x-3"
                    v-if="!detail.is_evaluated"
                  >
                    <a-popconfirm
                      placement="leftTop"
                      :title="$t('are_you_sure_to_delete')"
                      :ok-text="$t('yes')"
                      :cancel-text="$t('no')"
                      @confirm="deleteRecord(detail)"
                    >
                      <a href="#" :disabled="deleting">
                        <i
                          class="fas fa-circle-notch spin tw-wh"
                          v-if="deleting"
                        ></i>
                        <i class="fa fa-trash text-red" v-else></i>
                      </a>
                    </a-popconfirm>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end tw-p-4 tw-wh">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-panel>
    <a-drawer
      :title="$t('filter')"
      :closable="false"
      :visible="visible"
      :zIndex="1020"
      @close="() => (visible = false)"
      width="300px"
    >
      <div class="mb-3">
        <label class="form-col-label control-label">{{
          $t("testingCenter")
        }}</label>
        <ts-branch-filter @filter="(value) => (branch_id = value)" />
      </div>
      <div class="mb-3">
        <label class="form-col-label control-label">{{
          $t("examRegister.examDate")
        }}</label>
        <a-range-picker
          v-model="dateRange"
          :allowClear="false"
          format="DD-MM-YYYY"
          valueFormat="DD-MM-YYYY"
          @change="fetchData()"
        />
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapState } from "vuex";
export default {
  name: "examRegisterIndex",
  data() {
    return {
      loading: false,
      showForm: false,
      deleting: false,
      visible: false,
      branch_id: [],
    };
  },
  computed: {
    ...mapState("academic/examRegister", ["resources", "pagination"]),
    search: {
      get() {
        return this.$store.state.academic.examRegister.search;
      },
      set(newValue) {
        this.$store.commit("academic/examRegister/SET_SEARCH", newValue);
        this.$store.commit("academic/examRegister/RESET_CURRENT_PAGE");
      },
    },
    dateRange: {
      get() {
        return this.$store.state.academic.examRegister.dateRange;
      },
      set(newValue) {
        this.$store.commit("academic/examRegister/SET_DATE_RANGE", newValue);
      },
    },
    columns() {
      return [
        {
          name: this.$t("examRegister.testingCenter"),
          style: "text-align:center;width:150px;white-space: nowrap;",
        },
        {
          name: this.$t("examRegister.examinationEvent"),
          style: "white-space: nowrap",
        },
        {
          name: this.$t("examRegister.skill"),
          style: "white-space: nowrap",
        },
        {
          name: this.$t("examRegister.trainer"),
          style: "white-space: nowrap",
        },
        {
          name: this.$t("examRegister.trainer2"),
          style: "white-space: nowrap",
        },
        {
          name: this.$t("examRegister.trainer3"),
          style: "white-space: nowrap",
        },
        {
          name: this.$t("examRegister.room"),
          style: "text-align:center;white-space: nowrap;width: 100px",
        },
        {
          name: this.$t("examRegister.examDate"),
          style: "width: 150px;text-align:center;white-space: nowrap",
        },
        {
          name: this.$t("examRegister.timeStart"),
          style: "width: 150px;text-align:center;white-space: nowrap",
        },
        {
          name: this.$t("examRegister.timeStop"),
          style: "width: 150px;text-align:center;white-space: nowrap",
        },
      ];
    },
  },
  methods: {
    fetchData(attributes) {
      this.loading = true;
      this.$store
        .dispatch("academic/examRegister/fetch", {
          ...attributes,
          branch_id: this.branch_id,
        })
        .then(() => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.$notify({ type: "error", text: error.message });
        });
    },
    clearEdit() {
      this.$store.commit("academic/examRegister/SET_EDIT_DATA", {});
      this.showForm = false;
      this.fetchData();
    },
    deleteRecord(record) {
      this.deleting = true;
      this.$store
        .dispatch("academic/examRegister/destroy", record.exam_register_id)
        .then((response) => {
          this.$notify({ type: "success", text: response.message });
          this.fetchData();
        })
        .catch((error) => {
          this.$notify({ type: "error", text: error.message });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
  watch: {
    search: debounce(function () {
      this.fetchData();
    }, 500),
    branch_id: debounce(function (value) {
      this.fetchData({ branch_id: value });
    }, 500),
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fetchData();
    });
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit("academic/examRegister/RESET_STATE");
    next();
  },
};
</script>

<style lang="scss" scoped>
</style>